import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { cores } from '../../theme';

import {
    Typography, Container, Box, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, Alert, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormHelperText,
    Avatar, Fade, Divider, DialogContentText, Chip, Menu, MenuItem, ListItemIcon, ListItemText, useTheme
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack,
    Add as AddIcon,
    Close as CloseIcon,
    Tune as TuneIcon,
    Warning as WarningIcon,
    Check as CheckIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const OpcoesRespostaGerenciamentoPagina = () => {
    const [opcoes, setOpcoes] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [modalAberto, setModalAberto] = useState(false);
    const [editando, setEditando] = useState(null);
    const [textoOpcao, setTextoOpcao] = useState('');
    const [valorNumerico, setValorNumerico] = useState('');
    const [confirmarExclusao, setConfirmarExclusao] = useState({ aberto: false, opcaoId: null, textoOpcao: '' });
    const [menuAnchorEl, setMenuAnchorEl] = useState({
        id: null,
        element: null
    });
    const navegar = useNavigate();
    const theme = useTheme();

    const buscarOpcoes = useCallback(async () => {
        try {
            const resposta = await api.get('/api/opcoes-resposta');
            setOpcoes(resposta.data);
        } catch (err) {
            setErro('Falha ao carregar as opções de resposta.');
        } finally {
            if (carregando) setCarregando(false);
        }
    }, [carregando]);

    useEffect(() => {
        buscarOpcoes();
    }, [buscarOpcoes]);

    const abrirMenuAcoes = (event, opcaoId) => {
        setMenuAnchorEl({
            id: opcaoId,
            element: event.currentTarget
        });
    };

    const fecharMenuAcoes = () => {
        setMenuAnchorEl({
            id: null,
            element: null
        });
    };

    const abrirModalParaCriar = () => {
        setEditando(null);
        setTextoOpcao('');
        setValorNumerico('');
        setErro('');
        setModalAberto(true);
    };

    const abrirModalParaEditar = (opcao) => {
        setEditando(opcao);
        setTextoOpcao(opcao.texto_opcao);
        setValorNumerico(opcao.valor_numerico);
        setErro('');
        setModalAberto(true);
        fecharMenuAcoes();
    };

    const fecharModal = () => {
        setModalAberto(false);
        setEditando(null);
    };

    const lidarComSalvar = async () => {
        if (!textoOpcao || valorNumerico === '') return setErro("Todos os campos são obrigatórios.");
        const payload = { texto_opcao: textoOpcao, valor_numerico: Number(valorNumerico) };
        try {
            if (editando) {
                await api.put(`/api/opcoes-resposta/${editando.id}`, payload);
            } else {
                await api.post('/api/opcoes-resposta/criar', [payload]);
            }
            fecharModal();
            buscarOpcoes();
        } catch (err) {
            setErro(err.response?.data?.erro || "Ocorreu um erro ao salvar.");
        }
    };

    const abrirConfirmacaoExclusao = (opcaoId, textoOpcao) => {
        setConfirmarExclusao({
            aberto: true,
            opcaoId,
            textoOpcao
        });
        fecharMenuAcoes();
    };

    const fecharConfirmacaoExclusao = () => {
        setConfirmarExclusao({
            aberto: false,
            opcaoId: null,
            textoOpcao: ''
        });
    };

    const lidarComDelecao = async () => {
        try {
            await api.delete(`/api/opcoes-resposta/${confirmarExclusao.opcaoId}`);
            fecharConfirmacaoExclusao();
            buscarOpcoes();
        } catch (err) {
            setErro(err.response?.data?.erro || "Erro ao deletar opção de resposta.");
            fecharConfirmacaoExclusao();
        }
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/gerenciar')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Escala de Avaliação
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                Configure as opções de resposta disponíveis para avaliações
                            </Typography>
                        </Box>
                    </Box>

                    <Fade in={true} timeout={600}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, sm: 3 },
                                mb: 3,
                                bgcolor: '#f8fafc',
                                borderRadius: 2,
                                border: `1px solid ${cores.cinza.claro}`,
                                boxShadow: cores.sombras.suave
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Avatar
                                    sx={{
                                        mr: 2,
                                        bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                        color: cores.primaria.principal
                                    }}
                                >
                                    <TuneIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="overline" sx={{ color: cores.cinza.medio, fontWeight: 600 }}>
                                        CONFIGURAÇÃO DA ESCALA
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: cores.cinza.escuro, mt: 0.5 }}>
                                        Aqui você define a escala de notas usada em todas as avaliações. O 'Valor Numérico' representa o percentual de acerto do critério.
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: cores.cinza.escuro, mt: 1 }}>
                                        Por exemplo, se a nota máxima for 10, um valor de 10 significa que o aluno atingiu 100% do peso daquele critério na nota final.
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Fade>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2
                    }}>
                        <Box>
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    color: cores.primaria.principal,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                }}
                            >
                                Opções de Resposta
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {opcoes.length} {opcoes.length === 1 ? 'opção disponível' : 'opções disponíveis'}
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            onClick={abrirModalParaCriar}
                            startIcon={<AddIcon />}
                            sx={{
                                bgcolor: cores.primaria.principal,
                                borderRadius: 2,
                                boxShadow: cores.sombras.suave,
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    bgcolor: cores.primaria.escuro,
                                }
                            }}
                        >
                            Adicionar Nova Opção
                        </Button>
                    </Box>

                    {erro && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: cores.erro.claro,
                                borderColor: cores.erro.medio,
                                color: cores.erro.escuro,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {erro}
                        </Alert>
                    )}

                    <Fade in={true} timeout={500}>
                        <TableContainer
                            component={Paper}
                            sx={{
                                borderRadius: 2,
                                boxShadow: cores.sombras.suave,
                                overflow: 'hidden',
                                border: `1px solid ${cores.cinza.claro}`,
                            }}
                        >
                            <Table>
                                <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal, width: '50%' }}>
                                            Texto da Opção
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal, width: '30%' }}>
                                            Valor Numérico
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: cores.primaria.principal }}>
                                            Ações
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {opcoes.map((opcao, index) => (
                                        <Fade in={true} timeout={300 + index * 100} key={opcao.id}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: '#f8fafc',
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize: '1rem',
                                                                color: cores.cinza.escuro
                                                            }}
                                                        >
                                                            {opcao.texto_opcao}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={opcao.valor_numerico}
                                                        sx={{
                                                            fontWeight: 600,
                                                            bgcolor: opcao.valor_numerico === 10 ? `rgba(${parseInt(cores.sucesso.principal.slice(1, 3), 16)}, ${parseInt(cores.sucesso.principal.slice(3, 5), 16)}, ${parseInt(cores.sucesso.principal.slice(5, 7), 16)}, 0.1)` : `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                            color: opcao.valor_numerico === 10 ? cores.sucesso.principal : cores.secundaria.principal,
                                                            border: opcao.valor_numerico === 10 ? `1px solid rgba(${parseInt(cores.sucesso.principal.slice(1, 3), 16)}, ${parseInt(cores.sucesso.principal.slice(3, 5), 16)}, ${parseInt(cores.sucesso.principal.slice(5, 7), 16)}, 0.2)` : `1px solid rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.2)`
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        onClick={(e) => abrirMenuAcoes(e, opcao.id)}
                                                        size="small"
                                                        sx={{
                                                            border: `1px solid ${cores.cinza.claro}`,
                                                            '&:hover': {
                                                                bgcolor: '#f1f5f9',
                                                            }
                                                        }}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        </Fade>
                                    ))}
                                    {opcoes.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body1" sx={{ color: cores.cinza.medio }}>
                                                    Nenhuma opção de resposta encontrada.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Fade>
                </Paper>
            </Fade>

            {/* Modal para criar/editar opção */}
            <Dialog
                open={modalAberto}
                onClose={fecharModal}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`, color: cores.primaria.principal }}>
                            {editando ? <EditIcon /> : <AddIcon />}
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {editando ? 'Editar Opção de Resposta' : 'Adicionar Nova Opção'}
                        </Typography>
                    </Box>
                    <IconButton onClick={fecharModal} size="small" sx={{ color: cores.cinza.medio }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 3 }}>
                    {erro && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: cores.erro.claro,
                                borderColor: cores.erro.medio,
                                color: cores.erro.escuro,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {erro}
                        </Alert>
                    )}

                    <Typography
                        variant="body2"
                        sx={{ mb: 2, color: cores.cinza.medio }}
                    >
                        {editando
                            ? 'Atualize os detalhes da opção de resposta.'
                            : 'Configure os detalhes para a nova opção de resposta.'
                        }
                    </Typography>

                    <TextField
                        autoFocus
                        label="Texto da Opção"
                        fullWidth
                        variant="outlined"
                        value={textoOpcao}
                        onChange={(e) => setTextoOpcao(e.target.value)}
                        sx={{ mb: 3 }}
                        placeholder="Ex: Excelente"
                        InputProps={{
                            sx: { borderRadius: 2 }
                        }}
                        helperText="Este texto será exibido como opção para os avaliadores"
                    />

                    <TextField
                        label="Valor Numérico"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={valorNumerico}
                        onChange={(e) => setValorNumerico(e.target.value)}
                        InputProps={{
                            sx: { borderRadius: 2 }
                        }}
                        placeholder="Ex: 10"
                        helperText="Use um valor entre 0 e 10, onde 10 representa 100% do peso do critério"
                    />

                    <Paper
                        elevation={0}
                        sx={{
                            mt: 3,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`,
                            border: `1px solid rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.2)`,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: cores.secundaria.principal }}>
                            <strong>Dica:</strong> Um sistema comum é usar valores como 10 (Excelente), 7 (Bom), 5 (Regular), 3 (Insuficiente) e 0 (Muito insuficiente).
                        </Typography>
                    </Paper>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={fecharModal}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: cores.cinza.claro,
                            color: cores.cinza.escuro,
                            '&:hover': {
                                borderColor: cores.cinza.medio,
                                bgcolor: 'rgba(203, 213, 225, 0.1)'
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComSalvar}
                        variant="contained"
                        startIcon={<CheckIcon />}
                        sx={{
                            borderRadius: 2,
                            bgcolor: cores.primaria.principal,
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: cores.primaria.escuro,
                            }
                        }}
                    >
                        {editando ? 'Atualizar' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de confirmação de exclusão */}
            <Dialog
                open={confirmarExclusao.aberto}
                onClose={fecharConfirmacaoExclusao}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte,
                        maxWidth: 450
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: cores.erro.claro,
                    color: cores.erro.escuro,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    py: 2
                }}>
                    <WarningIcon color="error" fontSize="small" />
                    Confirmar Exclusão
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <DialogContentText sx={{ color: cores.cinza.escuro, mb: 2 }}>
                        Você está prestes a excluir a opção de resposta:
                    </DialogContentText>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: '#f8fafc',
                            borderRadius: 2,
                            border: `1px solid ${cores.cinza.claro}`,
                            mb: 2
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: cores.cinza.escuro,
                                fontStyle: 'italic'
                            }}
                        >
                            "{confirmarExclusao.textoOpcao}"
                        </Typography>
                    </Paper>
                    <DialogContentText sx={{
                        color: cores.erro.escuro,
                        bgcolor: cores.erro.claro,
                        p: 2,
                        borderRadius: 2,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <WarningIcon fontSize="small" sx={{ color: cores.erro.medio }} />
                        Esta ação não poderá ser desfeita. Avaliações existentes que usam esta opção poderão ser afetadas.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={fecharConfirmacaoExclusao}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            color: cores.cinza.escuro,
                            borderColor: cores.cinza.claro,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: cores.cinza.medio
                            },
                            px: 2
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComDelecao}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                            px: 3,
                            '&:hover': {
                                backgroundColor: cores.erro.escuro,
                                boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.3)`
                            }
                        }}
                    >
                        Excluir Opção
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Menu de ações */}
            <Menu
                anchorEl={menuAnchorEl.element}
                open={Boolean(menuAnchorEl.element)}
                onClose={fecharMenuAcoes}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        borderRadius: 2,
                        minWidth: 180,
                        boxShadow: cores.sombras.medio,
                        '& .MuiList-root': {
                            py: 1
                        }
                    }
                }}
            >
                <MenuItem
                    onClick={() => {
                        const opcao = opcoes.find(o => o.id === menuAnchorEl.id);
                        abrirModalParaEditar(opcao);
                    }}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" sx={{ color: cores.primaria.principal }} />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const opcao = opcoes.find(o => o.id === menuAnchorEl.id);
                        abrirConfirmacaoExclusao(menuAnchorEl.id, opcao?.texto_opcao || '');
                    }}
                    sx={{
                        py: 1,
                        color: cores.erro.principal
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: cores.erro.principal }} />
                    </ListItemIcon>
                    <ListItemText>Excluir</ListItemText>
                </MenuItem>
            </Menu>
        </Container>
    );
};

export default OpcoesRespostaGerenciamentoPagina;